import { useState } from "react";
import { useParams } from "react-router";
import { post } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useValidator } from "@common/hooks/validation";
import { useTranslation } from "@localization/useTranslation";
import { getEnumValues } from "@common/enumOperations";
import { RiskObservationSource } from "../riskObservation/riskObservationDialog/RiskObservationDialog.types";
import {
    PublicRiskObservationForm,
    PublicRiskObservationLinkViewModel,
    PublicRiskObservationState,
    reportObservationFormSteps,
    ReportObservationStep,
} from "./PublicRiskObservation.types";

const reporterNameStorageKey = "riskObservationReporterName";
const reporterEmailStorageKey = "riskObservationReporterEmail";

const sourceHelperKeys: Record<RiskObservationSource, string> = {
    [RiskObservationSource.Manual]: "reportObservationSourceManualHelper",
    [RiskObservationSource.Meeting]: "reportObservationSourceMeetingHelper",
    [RiskObservationSource.Audit]: "reportObservationSourceAuditHelper",
    [RiskObservationSource.ThreatIntelligence]: "reportObservationSourceThreatIntelligenceHelper",
    [RiskObservationSource.Incident]: "reportObservationSourceIncidentHelper",
};

const sourceEmoji: Record<RiskObservationSource, string> = {
    [RiskObservationSource.Manual]: "📝",
    [RiskObservationSource.Meeting]: "🗣️",
    [RiskObservationSource.Audit]: "🔍",
    [RiskObservationSource.ThreatIntelligence]: "🌐",
    [RiskObservationSource.Incident]: "⚠️",
};

interface PublicRiskObservationValidatedFields {
    name: string;
    observedBy: string;
    observedByEmail: string;
}

const validatedFieldNames: Array<keyof PublicRiskObservationValidatedFields> = ["name", "observedBy", "observedByEmail"];

function readRememberedReporterName() {
    return localStorage.getItem(reporterNameStorageKey) ?? "";
}

function readRememberedReporterEmail() {
    return localStorage.getItem(reporterEmailStorageKey) ?? "";
}

// The observation is already saved by the time we get here, so a browser that blocks storage
// (cookies disabled, some private modes) must not turn a successful report into an error.
function rememberReporter(name: string, email: string) {
    try {
        localStorage.setItem(reporterNameStorageKey, name);
        localStorage.setItem(reporterEmailStorageKey, email);
    } catch {
        // Losing the convenience of remembered contact details is not worth failing the report over.
    }
}

function createEmptyForm(): PublicRiskObservationForm {
    return {
        name: "",
        description: "",
        source: RiskObservationSource.Manual,
        observedBy: readRememberedReporterName(),
        observedByEmail: readRememberedReporterEmail(),
    };
}

export function usePublicRiskObservationDataMapping() {
    const { externalId } = useParams<{ externalId: string }>();
    const { translateString, translateRiskObservationSource } = useTranslation();
    const url = `/public-observation/${externalId}`;

    const [step, setStep] = useState(ReportObservationStep.Welcome);
    const [form, setForm] = useState<PublicRiskObservationForm>(createEmptyForm());
    const hasRememberedReporter = readRememberedReporterName().length > 0 || readRememberedReporterEmail().length > 0;

    const { data, isLoading } = useDotLegalQuery<PublicRiskObservationLinkViewModel>(url, { refetchOnWindowFocus: false });

    const validation = useValidator<PublicRiskObservationValidatedFields>((validators) => ({
        name: validators.validateNotEmpty((x) => x.name, translateString("reportObservationNameLabel")),
        observedBy: validators.validateNotEmpty((x) => x.observedBy, translateString("name")),
        observedByEmail: validators.validateEmail((x) => x.observedByEmail, translateString("email")),
    }));

    validation.check({ name: form.name, observedBy: form.observedBy, observedByEmail: form.observedByEmail });

    const reportMutation = useDotLegalMutation(() => post<{}>(url, form), {
        onSuccess: () => {
            rememberReporter(form.observedBy.trim(), form.observedByEmail.trim());

            setStep(ReportObservationStep.Success);
        },
    });

    const sourceOptions = getEnumValues(RiskObservationSource).map((source) => ({
        source,
        name: translateRiskObservationSource(source),
        helperText: translateString(sourceHelperKeys[source]),
        emoji: sourceEmoji[source],
    }));

    const formStepIndex = reportObservationFormSteps.indexOf(step);

    const goToObservationStep = () => setStep(ReportObservationStep.Observation);

    const goToSourceStep = () => {
        if (validation.hasErrors("name")) {
            validation.showErrorFor("name");
            return;
        }

        setStep(ReportObservationStep.Source);
    };

    const goToReporterStep = () => setStep(ReportObservationStep.Reporter);

    const submit = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        reportMutation.mutateAsync();
    };

    // The reporter's contact details are deliberately carried over so a second report does not retype them.
    const resetFormAndGoTo = (nextStep: ReportObservationStep) => {
        setForm({ ...createEmptyForm(), observedBy: form.observedBy, observedByEmail: form.observedByEmail });
        // setShowErrors(false) leaves the per field flags from showErrorFor untouched, so the emptied
        // headline would keep its warning from the previous report without hiding them explicitly.
        validation.setShowErrors(false);
        validation.hideErrorsFor(validatedFieldNames);
        setStep(nextStep);
    };

    return {
        isLoading,
        isLinkActive: data?.state === PublicRiskObservationState.Ready,
        customerName: data?.customerName,
        emailLogoBlobId: data?.emailLogoBlobId,
        step,
        stepNumber: formStepIndex < 0 ? undefined : formStepIndex + 1,
        form,
        validation,
        sourceOptions,
        selectedSourceName: translateRiskObservationSource(form.source),
        hasRememberedReporter,
        isSubmitting: reportMutation.isLoading,
        setName: (name: string) => setForm({ ...form, name }),
        setDescription: (description: string) => setForm({ ...form, description }),
        setSource: (source: RiskObservationSource) => setForm({ ...form, source }),
        setObservedBy: (observedBy: string) => setForm({ ...form, observedBy }),
        setObservedByEmail: (observedByEmail: string) => setForm({ ...form, observedByEmail }),
        goToObservationStep,
        goToSourceStep,
        goToReporterStep,
        goBackToSourceStep: () => setStep(ReportObservationStep.Source),
        submit,
        startAnotherReport: () => resetFormAndGoTo(ReportObservationStep.Observation),
        finish: () => resetFormAndGoTo(ReportObservationStep.Welcome),
    };
}
